/**
 * Effect の中のプレースホルダ置換。
 *
 * 画面定義（JSON）は「何を変えるか」だけを書き、実際の値は実行時に決まる。
 *   $value  … 入力欄に入れた値
 *   $id     … 表の行の id
 *   $order  … 並べ替え先の順序
 *   $device … いま開いている機器の id
 *             （deviceKind で選ぶ画面は複数の機器で共用するため、
 *              画面定義に機器 id を書けない。無線 AP の画面がこれ）
 *
 * 文字列がプレースホルダ「そのもの」なら、型を保ったまま置き換える
 * （'$value' → 6 という数値になる。JSON には数値の穴を書けないため）。
 * 文の一部として含まれている場合は、文字列として差し込む。
 */

import type { Effect } from '../../types/scenario';

export type PlaceholderValue = string | number | boolean;
export type PlaceholderMap = Record<string, PlaceholderValue>;

/**
 * この仕組みが埋められるプレースホルダの一覧（＝実装が対応している穴）。
 *
 * **データ（admin-screens.json）に新しい穴を書いたら、ここにも足すこと。**
 * 足し忘れは tests/ui/admin-real-screens.test.ts が検出する。
 */
export const PLACEHOLDER_KEYS = ['$value', '$id', '$order', '$device'] as const;

function substituteString(text: string, vars: PlaceholderMap): unknown {
  // 「$value」だけで出来ている場合は型ごと差し替える
  const exact = Object.prototype.hasOwnProperty.call(vars, text) ? vars[text] : undefined;
  if (exact !== undefined) return exact;

  let result = text;
  for (const key of Object.keys(vars)) {
    if (!result.includes(key)) continue;
    result = result.split(key).join(String(vars[key]));
  }
  return result;
}

/** 値を深く辿ってプレースホルダを置き換えた複製を返す（元の値は変えない） */
export function substitute(value: unknown, vars: PlaceholderMap): unknown {
  if (typeof value === 'string') return substituteString(value, vars);
  if (Array.isArray(value)) return value.map((item) => substitute(item, vars));
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(source)) {
      result[key] = substitute(source[key], vars);
    }
    return result;
  }
  return value;
}

/**
 * Effect 配列のプレースホルダを埋める。
 *
 * 画面定義は JSON なので、置換後の形が Effect であることを型では保証できない。
 * 変換はここ 1 か所に閉じ込め、applyEffect 側の網羅性チェックに委ねる。
 */
export function resolveEffects(effects: Effect[] | undefined, vars: PlaceholderMap): Effect[] {
  if (!effects || effects.length === 0) return [];
  const resolved = substitute(effects, vars) as Effect[];
  warnUnresolved(resolved);
  return resolved;
}

/** 置換されずに残った `$〜` を集める（データと実装のずれの早期発見用） */
function collectUnresolved(value: unknown, found: Set<string>): void {
  if (typeof value === 'string') {
    for (const hit of value.match(/\$[A-Za-z][A-Za-z0-9_]*/g) ?? []) found.add(hit);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectUnresolved(item, found);
    return;
  }
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    for (const key of Object.keys(source)) collectUnresolved(source[key], found);
  }
}

/**
 * 埋め残しがあれば警告する。
 *
 * 埋め残した Effect は「存在しない機器」を指すことになり、
 * 世界は何も変わらないのに画面には「変更しました」と出てしまう。
 * ゲームは止めないが、開発中に必ず気づけるようにここで声を上げる。
 */
function warnUnresolved(effects: Effect[]): void {
  const found = new Set<string>();
  collectUnresolved(effects, found);
  if (found.size === 0) return;
  console.warn(
    `[admin] 置換されていないプレースホルダが残っています: ${[...found].join(', ')}` +
      `（対応しているのは ${PLACEHOLDER_KEYS.join(' / ')}）`,
    effects,
  );
}
